/**
 * Bundle 134 P0-A - Retrofit of the legacy Bundle 78 introductions
 * curation page.
 *
 * Previous shape: bare Bundle 78 curation console with a raw
 * `<Input>` for `identified_target_user_id`, reporter rendered as
 * email-only, no filter chips, no deep-link to the Bundle 131
 * detail experience. The Bundle 131/132/133 workflow endpoint
 * shipped without a corresponding upgrade to the LIST that feeds
 * matchmakers into it.
 *
 * Retrofit:
 *   - Filter chips (All / Pending review / Identifying / Identified
 *     / Call required) matching the Connections tab language.
 *   - Row card renders: reporter name (joined via the shared
 *     `fetch_reporter_enrichment` helper), gender/age/prior-events
 *     chips, target hint snippet, contact preference pill, interest
 *     type pill, submitted timestamp, status pill.
 *   - Whole row is a Link to `/admin/introductions/:intro_id`
 *     (the Bundle 131 detail workflow experience).
 *   - No bare `<Input>` for target_user_id on the list. The
 *     ONLY way to set a target is the "Pick this target" button
 *     on the detail page Suggestions tab (Bundle 134 P0-B).
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { RefreshCw, ArrowRight, PhoneForwarded, User } from 'lucide-react';

const STATUS_LABELS = {
  pending_review: 'Pending review',
  identifying: 'Identifying',
  identified: 'Identified',
  admin_call_required: 'Call required',
};

const STATUS_BADGE = {
  pending_review: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  identifying: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  identified: 'bg-blue-50 text-blue-800 border-blue-200',
  admin_call_required: 'bg-secondary-50 text-secondary-800 border-secondary-200',
};

const PILL_COLOR = {
  green: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  blue: 'bg-blue-50 text-blue-800 border-blue-200',
  red: 'bg-red-50 text-red-800 border-red-200',
  pink: 'bg-pink-50 text-pink-800 border-pink-200',
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  neutral: 'bg-neutral-50 text-neutral-700 border-neutral-200',
  grey: 'bg-neutral-50 text-neutral-500 border-neutral-200',
};

const fmtRel = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return 'just now';
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch (_e) { return ''; }
};

const AdminIntroductionsPage = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get('/admin/introductions',
        { params });
      setRows(data?.introductions || []);
      setCounts(data?.counts || {});
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Failed to load introductions',
      );
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 max-w-6xl mx-auto"
          data-testid="admin-introductions-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-secondary-800 mb-1"
                data-testid="intro-list-bundle-marker">
            Introductions * Bundle 134
          </div>
          <h1 className="font-display text-3xl font-bold text-primary-900">
            Missed connections queue
          </h1>
          <p className="text-neutral-600 text-sm mt-1 max-w-2xl">
            Prioritise pending requests, open a row to review the
            reporter context and scored candidate suggestions,
            then advance the workflow. Target user ids are set on
            the detail page - never typed by hand.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load}
                    disabled={loading}
                    data-testid="introductions-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading
             ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap"
            data-testid="introductions-status-filter">
        <button
          onClick={() => setStatusFilter('')}
          className={`px-3 py-1 rounded-full text-xs border
            ${!statusFilter
              ? 'bg-primary-900 text-white border-primary-900'
              : 'bg-white text-primary-800 border-neutral-200'}`}
          data-testid="introductions-filter-all"
        >All ({totalCount})</button>
        {Object.keys(STATUS_LABELS).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded-full text-xs border capitalize
              ${statusFilter === s
                ? 'bg-primary-900 text-white border-primary-900'
                : 'bg-white text-primary-800 border-neutral-200'}`}
            data-testid={`introductions-filter-${s}`}
          >
            {STATUS_LABELS[s]} ({counts[s] || 0})
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {rows.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-neutral-500 text-sm text-center"
                              data-testid="introductions-empty-state">
              No introductions in this filter yet.
            </CardContent>
          </Card>
        ) : null}
        {rows.map((r) => (
          <IntroductionCard key={r.id} row={r} />
        ))}
      </div>
    </div>
  );
};


const Pill = ({ spec, testid }) => {
  if (!spec) return null;
  const cls = PILL_COLOR[spec.color] || PILL_COLOR.neutral;
  return (
    <span data-testid={testid}
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${cls}`}>
      {spec.label}
    </span>
  );
};


const IntroductionCard = ({ row }) => {
  const reporter = row.reporter || {};
  const displayName = [reporter.first_name, reporter.last_name]
    .filter(Boolean).join(' ').trim();
  const target = row.identified_target_profile;
  const isCallRequired = row.status === 'admin_call_required';

  return (
    <Link
      to={`/admin/introductions/${row.id}`}
      className="block group"
      data-testid={`intro-list-row-${row.id}`}
    >
      <Card className="transition-colors group-hover:border-primary-300">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline"
                        className={`${STATUS_BADGE[row.status] || ''} capitalize`}
                        data-testid={`intro-list-status-${row.id}`}>
                {STATUS_LABELS[row.status] || row.status}
              </Badge>
              <span className="text-xs text-neutral-500 font-mono"
                        data-testid={`intro-list-event-${row.id}`}>
                {row.event_slug || row.event_id}
              </span>
              <span className="text-xs text-neutral-400"
                        data-testid={`intro-list-submitted-${row.id}`}>
                {fmtRel(row.created_at)}
              </span>
              {isCallRequired ? (
                <span className="text-[11px] text-secondary-800 inline-flex items-center gap-1">
                  <PhoneForwarded className="h-3 w-3" />
                  Awaiting call queue
                </span>
              ) : null}
            </div>
            <ArrowRight className="h-4 w-4 text-neutral-400 group-hover:text-primary-800" />
          </div>

          <div className="grid gap-3 md:grid-cols-[1fr_1fr]">
            <div className="text-sm">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                Reporter
              </div>
              <div className="text-primary-900 font-semibold inline-flex items-center gap-1"
                        data-testid={`intro-list-reporter-name-${row.id}`}>
                <User className="h-3 w-3 text-neutral-400" />
                {displayName || '(no name on file)'}
              </div>
              <div className="text-neutral-500 text-xs">
                {reporter.email || ''}
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {reporter.gender ? (
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${PILL_COLOR.neutral}`}
                            data-testid={`intro-list-reporter-gender-${row.id}`}>
                    {String(reporter.gender)}
                  </span>
                ) : null}
                {reporter.age_band ? (
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${PILL_COLOR.neutral}`}
                            data-testid={`intro-list-reporter-age-${row.id}`}>
                    {reporter.age_band}
                  </span>
                ) : null}
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${PILL_COLOR.neutral}`}
                          data-testid={`intro-list-reporter-prior-${row.id}`}>
                  Prior events: {reporter.prior_events_count || 0}
                </span>
                {reporter.allow_introductions === false ? (
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${PILL_COLOR.red}`}>
                    allow_introductions=NO
                  </span>
                ) : null}
              </div>
            </div>

            <div className="text-sm">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                Target hint
              </div>
              <div className="text-primary-900 line-clamp-2"
                        data-testid={`intro-list-hint-${row.id}`}>
                {row.description_snippet || row.target_hint?.description || '(no description)'}
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                <Pill spec={row.contact_preference}
                          testid={`intro-list-contact-pref-${row.id}`} />
                <Pill spec={row.interest_pill}
                          testid={`intro-list-interest-${row.id}`} />
              </div>
              {target ? (
                <div className="text-[11px] text-emerald-700 mt-2"
                          data-testid={`intro-list-target-profile-${row.id}`}>
                  Identified target: {target.first_name} {target.last_name}
                </div>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default AdminIntroductionsPage;
